import { useState, useContext } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import clsx from "clsx";
import { useFlutterwave, closePaymentModal } from "flutterwave-react-v3";
import { usePaystackPayment } from "react-paystack";
import { Arrow, FundCard } from "../../../components/svg/Wallet/Wallet";
import { Card } from "../../../components/Card";
import Button from "../../../components/button/Button";
import { AppLayout } from "../../../layout/AppLayout";
import { BaseInput } from "../../../components/input/baseInput";
import { Loader } from "../../../components/loader/Loader";
import { UserContext } from "../../../components/context/UseContext";
import { useVerifyCardPayment } from "../../../api/verifyCardPayment";

const FundWalletVia = [
  { label: "Flutterwave", value: "flutterwave" },
  { label: "Paystack", value: "paystack" },
];

export const CardTopUp = () => {
  const navigate = useNavigate();
  const { profile } = useContext(UserContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const { verifyCardPayment, loading } = useVerifyCardPayment();
  const amountParam = searchParams.get("amount");
  const currentStep = searchParams.get("step");
  const [amount, setAmount] = useState(amountParam || "");
  const [method, setMethod] = useState(FundWalletVia[0]);

  // Handle back navigation
  const handleBack = () => {
    if (currentStep === "payment-methods") {
      navigate(-1);
    } else {
      navigate("/settings/wallet");
    }
  };

  const handleAmountSubmit = () => {
    if (!amount || amount < 500 || amount > 99999) {
      toast.info("Please enter a valid amount between ₦500 and ₦99,999.");
      return;
    }
    setSearchParams({
      step: "payment-methods",
      amount: amount,
    });
  };

  const config = {
    public_key: "FLWPUBK_TEST-bb14670461348b5a927827da956ec325-X",
    tx_ref: Date.now(),
    amount: parseFloat(amountParam) || 100,
    currency: "NGN",
    payment_options: "card,mobilemoney,ussd, banktransfer",
    customer: {
      email: profile.email,
      phone_number: profile.phone || "+2347062986206",
      name: `${profile?.first_Name || "Anonymous"} ${
        profile?.last_Name || "User"
      }`,
    },
    customizations: {
      title: "Wallet Top Up",
      description: "Payment for wallet top up",
      logo: "https://st2.depositphotos.com/4403291/7418/v/450/depositphotos_74189661-stock-illustration-online-shop-log.jpg",
    },
  };

  const paystackConfig = {
    reference: new Date().getTime().toString(),
    email: profile.email,
    amount: parseFloat(amountParam) * 100 || 100 * 100,
    publicKey: "pk_test_e4ac7acbc8627f70face51c904aaf3171ed824e0",
    channels: ["card", "bank", "ussd", "qr", "bank_transfer"],
  };

  const handleFlutterPayment = useFlutterwave(config);
  const handlePaystackPayment = usePaystackPayment(paystackConfig);

  const handlePayment = () => {
    if (method.value === "flutterwave") {
      handleFlutterPayment({
        callback: async (response) => {
          if (response.status !== "completed") {
            toast.error("Payment failed. Please try again.");
            return;
          } else {
            const isSuccess = await verifyCardPayment(
              response.transaction_id,
              method.value
            );

            if (isSuccess && !loading) {
              navigate(`/settings/wallet`);
            }
          }
          closePaymentModal();
        },
      });
    } else if (method.value === "paystack") {
      handlePaystackPayment({
        onSuccess: async (response) => {
          if (response.status !== "success") {
            toast.error("Payment failed. Please try again.");
            return;
          } else {
            const isSuccess = await verifyCardPayment(
              response.reference,
              method.value
            );

            if (isSuccess && !loading) {
              navigate(`/settings/wallet`);
            }
          }
        },
        onClose: () => {
          // console.warn("Payment closed");
        },
      });
    }
  };

  const renderAmountStep = () => (
    <>
      <Card className="my-12">
        <BaseInput
          label="Enter Amount"
          type="number"
          placeholder="500 - 99,999"
          value={amount || amountParam || ""}
          onChange={(e) => {
            const value = e.target.value;
            setAmount(value);
            setSearchParams({
              amount: value,
            });
          }}
          className="border-b border-b-[#00000080] placeholder:text-[#00000080]"
          inputClass="bg-transparent !px-5"
          leadingIcon={
            <span className="absolute transform top-[58%] -translate-y-0 left-1">
              ₦
            </span>
          }
        />
      </Card>
      <Button
        onClick={handleAmountSubmit}
        text="Fund Account"
        className="!py-4 !rounded-[50px]"
      />
    </>
  );

  const renderPaymentMethods = () => (
    <>
      <div className="my-12 flex flex-col gap-5">
        {FundWalletVia?.map((x, i) => {
          return (
            <Card
              key={i}
              onClick={() => setMethod(x)}
              className={clsx(
                x.value === method.value ? "!border border-primary-main" : "",
                "flex items-center justify-between gap-3 !py-2.5 !pl-2.5 !pr-7 cursor-pointer"
              )}
            >
              <div className="flex items-center gap-3">
                <FundCard />
                <div>
                  <p className="text-base font-semibold text-primary-black mb-1">
                    {x.label}
                  </p>
                </div>
              </div>
            </Card>
          );
        })}
      </div>
      <Button
        onClick={handlePayment}
        text={
          loading ? <Loader type="bubbles" /> : `Proceed to ${method.label}`
        }
        className={clsx(loading ? "!py-0" : "!py-4 ", "!rounded-[50px]")}
      />
    </>
  );

  return (
    <AppLayout noNav bg={"!bg-[#F8F8F8]"}>
      <div className="min-h-screen px-4 pt-10">
        <div className="relative flex items-center justify-center">
          <Arrow
            onClick={handleBack}
            stroke="#000"
            className="text-lg absolute left-0 cursor-pointer"
          />
          <p className="font-bold text-xl text-primary-black">Fund Wallet</p>
        </div>

        {currentStep === "payment-methods"
          ? renderPaymentMethods()
          : renderAmountStep()}
      </div>
    </AppLayout>
  );
};
